import { Check, CheckCircle, Palette, Pencil } from "@tamagui/lucide-icons";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { Pressable } from "react-native";
import { TouchableOpacity } from "react-native-gesture-handler";
import { Circle, Image, View, XStack, YStack } from "tamagui";
import { useApi } from "../../core/apis";
import { useUploadCover } from "../../core/hooks/useUploadCover";
import { TagColorsAvailable } from "../../core/tags/types";
import {
  HelperType,
  type HelpItem,
  HelpItemRequiredType,
} from "../../helper/HelperManager";
import { useHelper } from "../../helper/useHelper";
import { DEFAULT_COVER_URL } from "../../plans/types";
import { Box } from "../../ui/Box";
import { Button } from "../../ui/Button";
import { EmojiPicker } from "../../ui/EmojiPicker";
import { Label } from "../../ui/Label";
import { log } from "../../ui/log";
import { ModuleIcon } from "../../ui/ModuleIcon";
import { TitleTextarea } from "../../ui/Title";
import { EditorForm, EditorRender } from "../../ui/YooptaEditor";
import type PlanInfoModuleModel from "./PlanInfoModuleModel";

export const PlanInfoQuickstartModuleUI = observer(
  ({ module }: { module: PlanInfoModuleModel }) => {
    const { modulesDataApi } = useApi();
    const [showEmojiSheet, setSetEmojiSheet] = useState(false);

    const uploadCoverButton = useUploadCover(
      (isUploading) => {
        console.info("got is uploading", isUploading);
      },
      (coverUrl) => {
        return module.setCoverUrl(coverUrl);
      },
      module.uid,
    );

    const [showColors, setShowColors] = React.useState(false);

    const moduleUid = module.uid;
    const plan = module.plan;

    React.useEffect(() => {
      const unsub = modulesDataApi.watchDataForModuleUid(moduleUid);
      return () => {
        log.network(
          `Stopped watchDataForModuleUid from moduleUid ${moduleUid}`,
        );
        unsub();
      };
    }, [modulesDataApi, moduleUid]);

    const items: HelpItem[] = React.useMemo(
      () => [
        {
          type: HelpItemRequiredType.Required,
          label: "Add a description using the editor below",
          isCompleted: () => {
            return plan.hasDescription;
          },
        },

        {
          type: HelpItemRequiredType.Tip,
          label: "Set a cover image or use a color to set the mood.",
          isCompleted: () => {
            return plan.hasCoverPhotoOrBackgroundColor;
          },
        },

        {
          type: HelpItemRequiredType.Tip,
          label: "Change the icon or rename your Planira",
          isCompleted: () => {
            return false;
          },
        },
      ],
      [plan.hasDescription, plan.hasCoverPhotoOrBackgroundColor],
    );

    const helper = useHelper(module.uid, HelperType.PlanInfo, items);

    const icon = module ? <ModuleIcon module={module} /> : <View />;

    let coverUrl = module.data.coverUrl;
    if (!coverUrl && !module.data.backgroundColor) {
      coverUrl = DEFAULT_COVER_URL;
    }

    return (
      <>
        <YStack gap="$space.medium">
          {helper}

          <Pressable onPress={() => setSetEmojiSheet(true)}>
            <Box>
              <XStack gap="$space.medium">
                <Label color={"$muted"}>Icon</Label>
                {icon}
              </XStack>
            </Box>
          </Pressable>

          <Box>
            <XStack gap="$space.medium" alignItems={"center"}>
              <Label color={"$muted"}>Title</Label>
              <TitleTextarea
                icon={Pencil}
                focusOnIconPress
                defaultValue={module.data.title ?? ""}
                submitOnBlur
                submitOnEnter
                blurOnSubmit
                onSubmitEditing={(e) => {
                  void module.setTitle(e.nativeEvent.text);
                }}
              />
            </XStack>
          </Box>

          <Box>
            <Label color={"$muted"}>Description</Label>
            <EditorForm
              id="description"
              value={toJS(module.data.descriptionYooptaEditor)}
              onSave={(data) => module.setDescriptionEditor(data ?? null)}
            />
          </Box>

          <Box>
            <Label color={"$muted"}>Background theme</Label>
            {!!coverUrl && (
              <Image
                style={{ aspectRatio: 3 / 1 }}
                objectFit="cover"
                source={{ uri: coverUrl }}
              />
            )}
            {!!module.data.backgroundColor && (
              <View
                style={{
                  height: 100,
                  backgroundColor: module.data.backgroundColor,
                }}
              />
            )}

            {showColors && (
              <Box>
                <XStack
                  width={"100%"}
                  flexWrap="wrap"
                  gap="$small"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <Palette size={"$medium"} />
                  {TagColorsAvailable.map((color) => {
                    const isCurrent =
                      color.color === module.data.backgroundColor;

                    return (
                      <TouchableOpacity
                        key={color.color}
                        onPress={() => {
                          module.setBackgroundColor(color.color);
                        }}
                      >
                        <Circle
                          key={color.color}
                          backgroundColor={color.color}
                          size="$medium"
                        >
                          {isCurrent && <CheckCircle color={color.textColor} />}
                        </Circle>
                      </TouchableOpacity>
                    );
                  })}
                </XStack>
              </Box>
            )}

            <XStack
              width={"100%"}
              gap={"$medium"}
              marginTop={"$small"}
              alignItems={"center"}
            >
              <View flex={1}>{uploadCoverButton}</View>
              <Label color={"$muted"}>or</Label>

              <View flex={1}>
                <Button
                  icon={showColors ? Check : Palette}
                  onPress={() => {
                    setShowColors(!showColors);
                  }}
                >
                  {showColors ? "Close" : "Color"}
                </Button>
              </View>
            </XStack>
          </Box>
        </YStack>

        <EmojiPicker
          sheetId="emoji"
          open={showEmojiSheet}
          onOpenChange={(open: boolean) => setSetEmojiSheet(open)}
          onSelect={(emoji) => {
            module?.setEmoji(emoji.emoji);
            setSetEmojiSheet(false);
          }}
        />
      </>
    );
  },
);

export const PlanInfoQuickstartModuleUIBlockUI = observer(
  ({ module }: { module: PlanInfoModuleModel }) => {
    return (
      <Box>
        {!!module.data.title && (
          <Label>
            {`${module.data.emoji ? `${module.data.emoji} ` : ""}${
              module.data.title
            }`}
          </Label>
        )}

        <EditorRender
          value={module.data.descriptionYooptaEditor}
          emptyText="Set a description..."
        />
      </Box>
    );
  },
);
